import React, { useState } from 'react';
import { Box, Grid, Modal, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BlueButton } from '../atoms';
import { CHECKOUT_URLS } from '../config';

const orderOptions: { [name: string]: string } = {
  subscriptionBundle: 'subscriptionBundle',
  subscriptionMineral: 'subscriptionMineral',
  subscriptionVitamin: 'subscriptionVitamin',
  singleBundle: 'singleBundle',
  singleMineral: 'singleMineral',
  singleVitamin: 'singleVitamin',
};

const orderNowLinks: { [name: string]: string } = {
  subscriptionBundle: CHECKOUT_URLS.blood_bundle_subscription.link,
  subscriptionMineral: CHECKOUT_URLS.blood_mineral_subscription.link,
  subscriptionVitamin: CHECKOUT_URLS.blood_vitamin_subscription.link,
  singleBundle: CHECKOUT_URLS.blood_bundle.link,
  singleMineral: CHECKOUT_URLS.blood_mineral_test.link,
  singleVitamin: CHECKOUT_URLS.blood_test.link,
};

export interface OrderBloodProps {
  bypassModal?: boolean;
}

const modalBox = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: 3,
  minWidth: { xs: '85%', md: '40%' },
  maxWidth: { xs: '85%', md: '50%' },
  minHeight: { xs: '40%', md: '40%' },
  maxHeight: { xs: '80%', md: '65%' },
  overflow: 'scroll',
  '&::-webkit-scrollbar': { width: 5 },
  '&::-webkit-scrollbar-track': { background: '#F8F8F8', borderRadius: 5 },
  '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: 5 },
  '&::-webkit-scrollbar:horizontal': {
    height: 0,
    width: 0,
    display: 'none',
  },
};

export const OrderBloodCard = (props: OrderBloodProps): JSX.Element => {
  const [selection, setSelection] = useState<string>(
    orderOptions.subscriptionBundle
  );
  const [checkoutLink, setCheckoutLink] = useState<string>(
    orderNowLinks.subscriptionBundle
  );
  const [modalOpen, setModalOpen] = useState<boolean>(true);
  const handleModalClose = () => setModalOpen(false);

  const renderCheckoutButton = (): JSX.Element => {
    return (
      <BlueButton
        label="Set up now"
        buttonfill={true}
        fullWidth={true}
        href={checkoutLink}
      />
    );
  };
  const renderCircle = (text: string, option: string): JSX.Element => {
    const selected = selection === option;
    const circleBorderColor = option.startsWith('subscription')
      ? '#0575FF'
      : 'black';
    return (
      <Typography
        sx={{
          fontSize: selected ? 16 : 12,
          color: selected ? 'text.primary' : 'text.secondary',
        }}
      >
        {selected ? (
          <CheckCircleIcon
            sx={{ color: 'info.main', position: 'relative', top: 6, pr: 1 }}
          />
        ) : (
          <div
            style={{
              borderRadius: '50%',
              border: `1px ${circleBorderColor} solid`,
              height: 20,
              width: 20,
              position: 'relative',
              top: 6,
              left: 3,
              marginRight: 8,
              display: 'inline-flex',
              cursor: 'pointer',
            }}
            onClick={() => {
              setSelection(option);
              setCheckoutLink(orderNowLinks[option]);
            }}
          />
        )}
        {text}
      </Typography>
    );
  };

  const content = (): JSX.Element => {
    return (
      <Box sx={props.bypassModal ? {} : modalBox}>
        <Grid container spacing={2} padding={props.bypassModal ? 0 : 5}>
          <Grid item xs={5}>
            <Box
              component="img"
              src="/images/blood-kits.png"
              alt="blood test package"
              sx={{
                maxHeight: { xs: 100, md: 150 },
                border: '1px solid black',
              }}
            />
          </Grid>
          <Grid item xs={7}>
            <Typography
              sx={{ fontSize: '20px', fontWeight: 700, lineHeight: '24px' }}
            >
              New Blood test membership includes 4 tests per year.
            </Typography>
            <Typography
              sx={{
                fontWeight: 300,
                lineHeight: '17.6px',
                fontSize: '16px',
                pt: 2,
              }}
            >
              Reach your peak performance by consistently updating your blood
              test results - a precisely dosed formula specific to your nutrient
              needs.
            </Typography>
          </Grid>
          <Grid item xs={5}>
            {renderCircle(
              'Include both tests',
              orderOptions.subscriptionBundle
            )}
            <ul>
              <li>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Save 10%
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Test vitamins and minerals
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Delivered Quarterly
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Cancel anytime
                </Typography>
              </li>
            </ul>
          </Grid>
          <Grid item xs={7}>
            {selection === orderOptions.subscriptionBundle &&
              renderCheckoutButton()}
          </Grid>
          <Grid item xs={5}>
            <Typography> Individual subscription </Typography>
            {renderCircle(
              'Blood Mineral test',
              orderOptions.subscriptionMineral
            )}
            {renderCircle(
              'Blood Vitamin test',
              orderOptions.subscriptionVitamin
            )}
          </Grid>
          <Grid item xs={7}>
            {selection.startsWith('subscription') &&
              selection !== orderOptions.subscriptionBundle &&
              renderCheckoutButton()}
          </Grid>
          <Grid item xs={5}>
            <Typography> One time </Typography>
            {renderCircle(
              'Blood Mineral and Vitamin test bundle',
              orderOptions.singleBundle
            )}
            {renderCircle('Blood Mineral test', orderOptions.singleMineral)}
            {renderCircle('Blood Vitamin test', orderOptions.singleVitamin)}
          </Grid>
          <Grid item xs={7}>
            {selection.startsWith('single') && renderCheckoutButton()}
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <>
      {!props.bypassModal && (
        <Modal open={modalOpen} onClose={handleModalClose}>
          {content()}
        </Modal>
      )}
      {props.bypassModal && content()}
    </>
  );
};
